import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockPrManagementService } from '@/services/mock/MockPrManagementService';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/lib/utils';
import { Layers, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

export const PrCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Form State
  const [requestNumber] = useState(`REQ-${1000 + Math.floor(Math.random() * 900)}`);
  const [title, setTitle] = useState('');
  const [justification, setJustification] = useState('');
  const [requiredDate, setRequiredDate] = useState('2026-09-30');
  const [priority, setPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'>('HIGH');

  const [companyCode, setCompanyCode] = useState('1000');
  const [plantCode, setPlantCode] = useState('PLANT-101');
  const [costCenterCode, setCostCenterCode] = useState('CC-ENG-402');
  const [purchasingOrg] = useState('PO-GLOBAL');
  const [purchasingGroup] = useState('001');

  // Line items state
  const [items, setItems] = useState<
    {
      materialCode: string;
      description: string;
      category: string;
      quantity: number;
      uom: string;
      unitPrice: number;
      costCenterCode: string;
      glAccountCode: string;
    }[]
  >([
    {
      materialCode: 'MAT-9901',
      description: 'ThinkPad P1 Gen 6 (Intel i9, 64GB RAM, 2TB SSD)',
      category: 'IT Hardware',
      quantity: 5,
      uom: 'EA',
      unitPrice: 24500,
      costCenterCode: 'CC-ENG-402',
      glAccountCode: 'GL-400100',
    },
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subtotal = items.reduce((acc, it) => acc + it.quantity * it.unitPrice, 0);
  const taxAmount = subtotal * 0.18;
  const totalAmount = subtotal + taxAmount;

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      {
        materialCode: 'MAT-8802',
        description: 'Chiller Unit Replacement HVAC',
        category: 'Facilities',
        quantity: 1,
        uom: 'JOB',
        unitPrice: 50000,
        costCenterCode: 'CC-ENG-402',
        glAccountCode: 'GL-500200',
      },
    ]);
  };

  const handleRemoveItem = (index: number) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!title || !justification) {
      setError('Title and Justification are required.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const formattedItems = items.map((it, idx) => ({
        id: `PR-ITEM-${Date.now()}-${idx}`,
        itemNumber: (idx + 1) * 10,
        materialCode: it.materialCode,
        description: it.description,
        category: it.category,
        quantity: it.quantity,
        uom: it.uom,
        unitPrice: it.unitPrice,
        totalPrice: it.quantity * it.unitPrice,
        currency: 'INR',
        plantCode,
        deliveryDate: requiredDate,
        costCenterCode: it.costCenterCode,
        glAccountCode: it.glAccountCode,
        accountAssignment: 'Cost Center (K)',
      }));

      const created = await mockPrManagementService.createPr({
        requestNumber,
        title,
        justification,
        requesterId: 'USR-101',
        requesterName: 'Sarah Jenkins',
        departmentName: 'Engineering',
        companyCode,
        plantCode,
        costCenterCode,
        purchasingOrg,
        purchasingGroup,
        priority,
        status: 'SUBMITTED',
        totalAmount,
        currency: 'INR',
        requiredDate,
        items: formattedItems,
        financialSummary: {
          subtotal,
          taxAmount,
          totalAmount,
          currency: 'INR',
          budgetLimit: 5000000,
          budgetConsumed: 1200000,
          availableBudget: 3500000,
          budgetStatus: 'SUFFICIENT',
        },
        sapValidation: {
          companyCode: true,
          plant: true,
          material: true,
          costCenter: true,
          glAccount: true,
          purchasingGroup: true,
          currency: true,
          quantity: true,
          deliveryDate: true,
          budget: true,
          isValid: true,
        },
        sapDetails: {
          sapStatus: 'NOT_CREATED',
          integrationStatus: 'PENDING',
        },
        lifecycle: [
          { key: 'REQ', label: '1. Requisition Created', status: 'COMPLETED', timestamp: new Date().toISOString().split('T')[0], docRef: requestNumber },
          { key: 'APP', label: '2. NFA Approval', status: 'ACTIVE' },
          { key: 'VAL', label: '3. SAP Validation', status: 'PENDING' },
          { key: 'SAP', label: '4. SAP PR Creation', status: 'PENDING' },
        ],
      });

      navigate(`/procurement/pr/${created.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create PR');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 pb-24 font-heading max-w-4xl mx-auto">
      {/* Top Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.PR.LIST)}
            className="h-9 rounded-xl border-border font-bold"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to PR List
          </Button>

          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2">
            <Layers className="h-6 w-6 text-primary" /> Create Purchase Requisition (PR)
          </h1>
        </div>

        <span className="font-mono text-xs font-bold text-primary bg-primary/10 px-3 py-1 rounded-full">
          Draft Ref: {requestNumber}
        </span>
      </div>

      {/* Step Stepper Bar */}
      <div className="grid grid-cols-4 gap-2 text-xs font-bold">
        {[
          { num: 1, label: '1. Basic Info' },
          { num: 2, label: '2. Org Scope' },
          { num: 3, label: '3. Line Items' },
          { num: 4, label: '4. Review & Submit' },
        ].map((s) => (
          <div
            key={s.num}
            onClick={() => setStep(s.num as any)}
            className={`p-3 rounded-xl border text-center cursor-pointer transition-all ${
              step === s.num
                ? 'border-primary bg-primary text-white shadow-2xs'
                : step > s.num
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700'
                : 'border-border bg-card text-foreground-secondary'
            }`}
          >
            {s.label}
          </div>
        ))}
      </div>

      {error && (
        <div className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300 font-bold text-xs">
          {error}
        </div>
      )}

      {/* STEP 1: BASIC INFORMATION */}
      {step === 1 && (
        <Card className="border-border rounded-2xl shadow-2xs p-6 space-y-4 text-xs">
          <div className="space-y-1">
            <label className="font-bold text-foreground block">Requisition Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Developer Laptop Refresh (ThinkPad P1 Gen 6)"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 font-bold text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="font-bold text-foreground block">Business Justification *</label>
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              rows={3}
              placeholder="Detailed justification explaining procurement requirement, urgency, and operational impact..."
              className="w-full rounded-xl border border-border bg-background p-3 font-medium text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-bold text-foreground block mb-1">Required Delivery Date *</label>
              <input
                type="date"
                value={requiredDate}
                onChange={(e) => setRequiredDate(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-mono font-bold text-foreground"
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Priority Level</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as any)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
              >
                <option value="LOW">LOW Priority</option>
                <option value="MEDIUM">MEDIUM Priority</option>
                <option value="HIGH">HIGH Priority</option>
                <option value="CRITICAL">CRITICAL Priority</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-border">
            <Button onClick={() => setStep(2)} className="rounded-xl font-bold bg-primary text-white">
              Next: Organizational Scope →
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 2: ORGANIZATIONAL SCOPE */}
      {step === 2 && (
        <Card className="border-border rounded-2xl shadow-2xs p-6 space-y-4 text-xs">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-bold text-foreground block mb-1">Company Code *</label>
              <input
                type="text"
                value={companyCode}
                onChange={(e) => setCompanyCode(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-mono font-bold text-foreground"
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Plant Code *</label>
              <select
                value={plantCode}
                onChange={(e) => setPlantCode(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-semibold text-foreground"
              >
                <option value="PLANT-101">PLANT-101 (Noida R&D Hub)</option>
                <option value="PLANT-102">PLANT-102 (Bengaluru Tech Park)</option>
              </select>
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Cost Center Code *</label>
              <select
                value={costCenterCode}
                onChange={(e) => setCostCenterCode(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-semibold text-foreground"
              >
                <option value="CC-ENG-402">CC-ENG-402 (Engineering R&D)</option>
                <option value="CC-FAC-201">CC-FAC-201 (Facilities Ops)</option>
                <option value="CC-IT-101">CC-IT-101 (Enterprise IT)</option>
              </select>
            </div>
          </div>

          <div className="flex justify-between pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl font-bold">
              ← Back
            </Button>
            <Button onClick={() => setStep(3)} className="rounded-xl font-bold bg-primary text-white">
              Next: Line Items →
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 3: LINE ITEMS */}
      {step === 3 && (
        <Card className="border-border rounded-2xl shadow-2xs p-6 space-y-4 text-xs">
          <div className="flex items-center justify-between pb-2 border-b border-border">
            <h3 className="font-bold text-sm text-foreground">Requisition Line Items</h3>
            <Button size="sm" onClick={handleAddItem} className="rounded-xl font-bold">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Line Item
            </Button>
          </div>

          {items.map((it, idx) => (
            <div key={idx} className="p-4 rounded-xl border border-border bg-muted/20 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-bold text-primary">Line Item #{idx + 1}</span>
                {items.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => handleRemoveItem(idx)} className="h-7 text-rose-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-foreground block mb-1">Material Code</label>
                  <input
                    type="text"
                    value={it.materialCode}
                    onChange={(e) => {
                      const val = e.target.value;
                      setItems((prev) => prev.map((item, i) => (i === idx ? { ...item, materialCode: val } : item)));
                    }}
                    className="w-full rounded-xl border border-border bg-background px-3 py-1.5 font-mono text-foreground"
                  />
                </div>

                <div>
                  <label className="font-bold text-foreground block mb-1">Description</label>
                  <input
                    type="text"
                    value={it.description}
                    onChange={(e) => {
                      const val = e.target.value;
                      setItems((prev) => prev.map((item, i) => (i === idx ? { ...item, description: val } : item)));
                    }}
                    className="w-full rounded-xl border border-border bg-background px-3 py-1.5 font-semibold text-foreground"
                  />
                </div>

                <div>
                  <label className="font-bold text-foreground block mb-1">Quantity</label>
                  <input
                    type="number"
                    value={it.quantity}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      setItems((prev) => prev.map((item, i) => (i === idx ? { ...item, quantity: val } : item)));
                    }}
                    className="w-full rounded-xl border border-border bg-background px-3 py-1.5 font-bold text-foreground"
                  />
                </div>

                <div>
                  <label className="font-bold text-foreground block mb-1">Unit Price (INR)</label>
                  <input
                    type="number"
                    value={it.unitPrice}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      setItems((prev) => prev.map((item, i) => (i === idx ? { ...item, unitPrice: val } : item)));
                    }}
                    className="w-full rounded-xl border border-border bg-background px-3 py-1.5 font-extrabold text-foreground"
                  />
                </div>
              </div>
            </div>
          ))}

          <div className="flex justify-between pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setStep(2)} className="rounded-xl font-bold">
              ← Back
            </Button>
            <Button onClick={() => setStep(4)} className="rounded-xl font-bold bg-primary text-white">
              Next: Review & Submit →
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 4: REVIEW & SUBMIT */}
      {step === 4 && (
        <Card className="border-border rounded-2xl shadow-2xs p-6 space-y-4 text-xs">
          <h3 className="font-bold text-sm text-foreground pb-2 border-b border-border">Requisition Review & Summary</h3>

          <div className="space-y-2">
            <p>Title: <strong className="text-foreground">{title || 'Untitled Requisition'}</strong></p>
            <p>Justification: <span className="text-foreground-secondary">{justification}</span></p>
            <p>Plant / Cost Center: <strong className="text-foreground font-mono">{plantCode} / {costCenterCode}</strong></p>
            <p>Total Estimated Value: <strong className="text-primary font-mono text-base">{formatCurrency(totalAmount, 'INR')}</strong></p>
          </div>

          <div className="flex justify-between pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setStep(3)} className="rounded-xl font-bold">
              ← Back to Items
            </Button>
            <Button
              disabled={isSubmitting}
              onClick={handleSubmit}
              className="rounded-xl font-bold bg-primary text-white shadow-2xs"
            >
              {isSubmitting ? 'Submitting PR...' : 'Submit Purchase Requisition'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};
